// =============================================================================
// stores/nightLight.store.ts
// R2 Night Light Navigation — Theater Mode with adaptive contrast
// Rule: backgrounds dim, text+icons STAY BRIGHT. Emergency = always visible.
// =============================================================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface NightLightSettings {
  // 0–1 multipliers — affect only backgrounds/decorative elements
  brightness:     number; // overall ambience (0.4 = very dark room, 1.0 = full)
  glow:           number; // text/icon halo intensity (0 = off, 1 = strong)
  contrast:       number; // text contrast punch (0.6 = soft, 1.0 = max sharp)
  laserIntensity: number; // panel border glow (0 = invisible, 1 = vivid)

  // Adaptive brightness for each UI layer (Adaptive Contrast spec)
  navBrightness:      number; // navigation tabs:       90–100%
  workspaceBrightness: number; // active workspace:      80–90%
  controlBrightness:  number; // secondary controls:    60–70%
  bgOpacity:          number; // panel backgrounds:     15–25%
  decoOpacity:        number; // decorative art:        5–10%

  // Safety lock — NEVER dim below this level, even if user drags sliders
  minTextContrast:    number; // 0–1, default 0.72 (WCAG AA floor)
  emergencyAlwaysFull: boolean; // SOS/alert/safety = always 100%
}

interface NightLightState extends NightLightSettings {
  enabled: boolean;
  setEnabled: (v: boolean) => void;
  update: (patch: Partial<NightLightSettings>) => void;
  reset: () => void;
  // Derived: returns safe clamped value for text opacity
  safeTextOpacity: (raw: number) => number;
}

export const DEFAULT_NIGHT_LIGHT: NightLightSettings = {
  brightness:          0.85,
  glow:                0.55,
  contrast:            0.92,
  laserIntensity:      0.6,
  navBrightness:       0.97,
  workspaceBrightness: 0.88,
  controlBrightness:   0.65,
  bgOpacity:           0.22,
  decoOpacity:         0.08,
  minTextContrast:     0.72,
  emergencyAlwaysFull: true,
};

export const useNightLightStore = create<NightLightState>()(
  persist(
    (set, get) => ({
      enabled: false,
      ...DEFAULT_NIGHT_LIGHT,

      setEnabled: (v) => set({ enabled: v }),

      update: (patch) =>
        set((s) => ({
          ...s,
          ...patch,
          // Enforce safety floors
          minTextContrast: Math.max(
            patch.minTextContrast ?? s.minTextContrast,
            0.6, // absolute minimum — phone in water must still be readable
          ),
          navBrightness: Math.max(patch.navBrightness ?? s.navBrightness, 0.85),
          workspaceBrightness: Math.max(patch.workspaceBrightness ?? s.workspaceBrightness, 0.65),
        })),

      reset: () => set({ ...DEFAULT_NIGHT_LIGHT }),

      safeTextOpacity: (raw) => {
        const { minTextContrast } = get();
        return Math.max(raw, minTextContrast);
      },
    }),
    { name: 'r2-night-light' },
  ),
);

// ── CSS Variable Injection ────────────────────────────────────────────────────
// Called once from NightLightProvider, updates :root CSS vars in real-time.
export function applyNightLightVars(s: NightLightSettings, enabled: boolean): void {
  const root = document.documentElement;
  const e = enabled;

  // Base multiplier — never goes below safe floor
  const brt = e ? s.brightness : 1.0;
  const glw = e ? s.glow       : 0.0;
  const las = e ? s.laserIntensity : 0.4;
  const ctr = e ? s.contrast   : 1.0;

  root.style.setProperty('--nl-brightness',   String(brt));
  root.style.setProperty('--nl-glow',         String(glw));
  root.style.setProperty('--nl-laser',        String(las));
  root.style.setProperty('--nl-contrast',     String(ctr));

  // Layer opacities
  root.style.setProperty('--nl-nav-bright',   e ? String(s.navBrightness)       : '1');
  root.style.setProperty('--nl-work-bright',  e ? String(s.workspaceBrightness) : '1');
  root.style.setProperty('--nl-ctrl-bright',  e ? String(s.controlBrightness)   : '0.7');
  root.style.setProperty('--nl-bg-opacity',   e ? String(s.bgOpacity)           : '0.72');
  root.style.setProperty('--nl-deco-opacity', e ? String(s.decoOpacity)         : '0.55');

  // Derived text colors — always bright enough to be legible
  const textAlpha = Math.max(s.navBrightness, s.minTextContrast);
  root.style.setProperty('--nl-text-primary', `rgba(240,244,255,${textAlpha})`);
  root.style.setProperty('--nl-text-secondary', `rgba(148,172,210,${Math.max(s.controlBrightness, s.minTextContrast)})`);

  // Tab glow/laser
  const tabGlow  = glw * 0.7;
  const laserClr = `rgba(37,181,168,${las * 0.85})`;
  root.style.setProperty('--nl-tab-glow',        `0 0 ${Math.round(glw * 14)}px rgba(37,181,168,${tabGlow})`);
  root.style.setProperty('--nl-tab-underline',   laserClr);
  root.style.setProperty('--nl-panel-border',    `rgba(37,181,168,${Math.max(las * 0.35, 0.12)})`);
  root.style.setProperty('--nl-panel-glow',      `rgba(37,181,168,${las * 0.06})`);

  // Decorative (Starry Night, backgrounds) — can go very dark
  root.style.setProperty('--nl-decore-filter',   e ? `brightness(${0.3 + s.decoOpacity})` : 'brightness(1)');
}
